import { UpdateUserPayload } from 'app/api/user'
import {
  formatApiStringToDate,
  formatDateToApiString,
} from 'app/utils/dateUtils'
import { BankAccount, Sponsorman } from 'EstraSponsor'
import { mergeWith } from 'lodash'

import { PersonHolderFormData, UserRegistrationFormData } from './formTypes'

export const adaptApiToFormData = (
  apiData: Sponsorman,
  defaultValues: UserRegistrationFormData
): UserRegistrationFormData => {
  // Merge data from Api and draft
  const merged = mergeWith(
    {},
    defaultValues,
    apiData,
    (draftValue, apiValue /* key*/) => {
      // Return default value if Api value is null or undefined
      return apiValue !== null && apiValue !== undefined ? apiValue : draftValue
    }
  )

  const adapted = {
    ...merged,
    id: undefined,
    province: undefined,
    city: undefined,
    company_province_id: merged.company_province?.id.toString(),
    company_city_id: merged.company_city?.id.toString(),
    company_cciaa_date: formatApiStringToDate(merged.company_cciaa_date),
    company_date_start: formatApiStringToDate(merged.company_date_start),

    fill_company_secondary_address: Boolean(
      merged.company_secondary_address_street
    ),
    company_secondary_province_id:
      merged.company_secondary_province?.id.toString(),
    company_secondary_city_id: merged.company_secondary_city?.id.toString(),

    holder_address: {
      ...merged.holder_address,
      province: undefined,
      city: undefined,
      birth_date: formatApiStringToDate(
        merged.holder_address.birth_date
      ) as Date,
      province_id: merged.holder_address?.province?.id.toString(),
      city_id: merged.holder_address?.city?.id.toString(),
    },

    fill_technical_director: Boolean(merged.fill_technical_director),
    technical_director_address: {
      ...merged.technical_director_address,
      birth_date: formatApiStringToDate(
        merged.technical_director_address?.birth_date
      ),
      province: undefined,
      city: undefined,
      province_id: merged.technical_director_address?.province?.id.toString(),
      city_id: merged.technical_director_address?.city?.id.toString(),
    },

    fill_technical_manager: Boolean(merged.fill_technical_manager),
    technical_manager_address: {
      ...merged.technical_manager_address,
      birth_date: formatApiStringToDate(
        merged.technical_manager_address.birth_date
      ),
      province: undefined,
      city: undefined,
      province_id: merged.technical_manager_address?.province?.id.toString(),
      city_id: merged.technical_manager_address?.city?.id.toString(),
    },

    fill_secondary_bank_account: Boolean(merged.fill_secondary_bank_account),
  }

  return adapted
}

export const adaptFormDataToAPI = (
  formData: UserRegistrationFormData
): UpdateUserPayload => {
  return {
    ...formData,
    company_vat:
      formData.registration_type === 'company'
        ? formData.company_vat
        : undefined,
    company_cciaa_date: formatDateToApiString(formData.company_cciaa_date),
    company_date_start: formatDateToApiString(formData.company_date_start),

    // Company Secondary
    // Workaround: Sets fields to empty string if fill boolean is false,
    // needed as a workaround for backend validation
    company_secondary_address_street: formData.fill_company_secondary_address
      ? formData.company_secondary_address_street
      : '',
    company_secondary_address_number: formData.fill_company_secondary_address
      ? formData.company_secondary_address_number
      : '',
    company_secondary_postal_code: formData.fill_company_secondary_address
      ? formData.company_secondary_postal_code
      : '',
    company_secondary_province_id: formData.fill_company_secondary_address
      ? formData.company_secondary_province_id
      : '',
    company_secondary_city_id: formData.fill_company_secondary_address
      ? formData.company_secondary_city_id
      : '',

    holder_address: {
      ...formData.holder_address,
      birth_date: formatDateToApiString(formData.holder_address.birth_date),
    },

    technical_director_address: formData.fill_technical_director
      ? {
          ...(formData.technical_director_address as PersonHolderFormData),
          birth_date: formatDateToApiString(formData.holder_address.birth_date),
        }
      : undefined,

    technical_manager_address: formData.fill_technical_manager
      ? {
          ...(formData.technical_manager_address as PersonHolderFormData),
          birth_date: formatDateToApiString(
            formData.holder_address.birth_date
          ) as string,
        }
      : undefined,

    bank_account_1: {
      ...formData.bank_account_1,
    },

    bank_account_2: formData.fill_secondary_bank_account
      ? {
          ...(formData.bank_account_2 as BankAccount),
        }
      : undefined,
  }
}
